import { Injectable } from "@angular/core";
import { VeriDate } from "@services/veriDate.service";
import { RouteVisitStatus } from "@tms/_enums/RouteVisitStatus";
import { IPlannedRouteVisit } from "@tms/_models/IPlannedRouteVisit";
import { IPlannedRouteDetail } from "./planned-route.service";

export interface IRouteDetailDisplay extends IPlannedRouteDetail {
    displayPlannedVisits: IRouteDetailVisitDisplay[];
    nextVisit?: IRouteDetailVisitDisplay;
    currentVisit?: IPlannedRouteVisit;
    hasVisibleVisits?: boolean;
    hasVisibleStops?: boolean;

    startTimeWindowPosition: number;
    endTimeWindowPosition: number;
}

export interface IRouteDetailVisitDisplay extends IPlannedRouteVisit {
    startPixelPosition?: number;
    endPixelPosition?: number;
    pixelWidth?: number;
    classSize?: string;
    actualPixelsTravelled?: number;
    pixelsToNextStop?: number;
    isVisible?: boolean;
    isLeftOfScreen?: boolean;

    status?: string;
    arrivalTimeDiff?: string;
    departureTimeDiff?: string;
    departureLate?: boolean;
    departureEarly?: boolean;
    nextVisit?: boolean;
}

@Injectable()
export class ActiveRoutesService {
    constructor(private veriDate: VeriDate) {
    }

    filterRouteByTextPredicate(route: IRouteDetailDisplay, searchString: string) {
        if (route.routeDisplayName?.toLowerCase().indexOf(searchString) >= 0 ||
            (route.vehicle && route.vehicle.Reg.toLowerCase().indexOf(searchString) >= 0) ||
            (route.driver && route.driver.Name.toLowerCase().indexOf(searchString) >= 0)) {
            return true;
        }

        return route.PlannedVisits.some(visit => visit.displayLabel.toLowerCase().indexOf(searchString) >= 0);
    }

    filterPlannedRoutes(routes: IRouteDetailDisplay[], searchString: string): IRouteDetailDisplay[] {
        if (!routes) {
            return;
        }

        if (!searchString || searchString.length === 0) {
            return routes;
        }

        searchString = searchString.toLowerCase();

        const filteredRoutes: IRouteDetailDisplay[] = [];

        routes.forEach(route => {
            if (this.filterRouteByTextPredicate(route, searchString)) {
                filteredRoutes.push(route);
            }
        });

        return filteredRoutes;
    }

    createDisplayPlannedVisitsOnRoute(route: IRouteDetailDisplay): IRouteDetailVisitDisplay[] {
        const updatedVisits = [...route.PlannedVisits];

        updatedVisits.forEach((visit, index) => {
            if (visit.Status === RouteVisitStatus.Arrived) {
                route.currentVisit = visit;
                route.nextVisit = route.PlannedVisits[index + 1];
            }
        });

        return updatedVisits;
    }

    isArrivalEarly(visit: IRouteDetailVisitDisplay): boolean {
        if (!visit) {
            return false;
        }

        const expectedArrivalTime = (this.veriDate.createMoment(visit.ActualArrivalTime) || this.veriDate.createMoment(visit.ArrivalEta)).clone();
        const bufferedArrivalTime = this.veriDate.createMoment(expectedArrivalTime).clone().add(visit.travelTimeBufferMins, "m");

        return this.veriDate.createMoment(visit.PlannedArrivalTime).isSameOrAfter(bufferedArrivalTime);
    }

    isArrivalLate(visit: IRouteDetailVisitDisplay): boolean {
        if (!visit) {
            return false;
        }

        const expectedArrivalTime = (this.veriDate.createMoment(visit.ActualArrivalTime) || this.veriDate.createMoment(visit.ArrivalEta)).clone();
        const bufferedArrivalTime = this.veriDate.createMoment(expectedArrivalTime).clone().add(-visit.travelTimeBufferMins, "m");

        return this.veriDate.createMoment(visit.PlannedArrivalTime).isSameOrBefore(bufferedArrivalTime);
    }

    // ---

    private isDepartureLate(visit: IRouteDetailVisitDisplay): boolean {
        let departureTime: moment.Moment;
        if (visit.ActualDepartureTime) {
            departureTime = this.veriDate.createMoment(visit.ActualDepartureTime).clone();
        }
        else {
            departureTime = (this.veriDate.createMoment(visit.ActualArrivalTime) || this.veriDate.createMoment(visit.ArrivalEta)).clone().add(visit.onSiteTimeMins, "m");
        }
        const bufferedDepartureTime = this.veriDate.createMoment(visit.PlannedDepartureTime).clone().add(visit.onSiteTimeBufferMins, "m");

        return departureTime.isSameOrAfter(bufferedDepartureTime);
    }

    private isDepartureEarly(visit: IRouteDetailVisitDisplay): boolean {
        let departureTime: moment.Moment;

        if (visit.ActualDepartureTime) {
            departureTime = this.veriDate.createMoment(visit.ActualDepartureTime).clone();
        }
        else {
            departureTime = (this.veriDate.createMoment(visit.ActualArrivalTime) || this.veriDate.createMoment(visit.ArrivalEta)).clone().add(visit.onSiteTimeMins, "m");
        }

        const bufferedDepartureTime = this.veriDate.createMoment(visit.PlannedDepartureTime).clone().add(-visit.onSiteTimeBufferMins, "m");

        return departureTime.isSameOrBefore(bufferedDepartureTime);
    }

    updateRouteVisits(route: IRouteDetailDisplay) {

        var lastVisit: IRouteDetailVisitDisplay;

        route.displayPlannedVisits.forEach((visit, i) => {
            const isFirstVisit = i === 0;
            const isFinalVisit = i === route.PlannedVisits.length - 1;

            this.updateRouteVisitTimes(visit, lastVisit, isFirstVisit, isFinalVisit);
            this.updateRouteVisitState(visit, lastVisit, route, isFinalVisit);

            if (i === 0) {
                visit.displayArrivalLabel = "--";
            }
            else if (visit.ActualArrivalTime) {
                visit.displayArrivalLabel = this.veriDate.formatDate(visit.ActualArrivalTime, "time");
            }
            else {
                visit.displayArrivalLabel = visit.status || "Planned";
            }

            if (i === route.PlannedVisits.length - 1) {
                visit.displayDepartureLabel = "--";
            }
            else if (visit.ActualDepartureTime) {
                visit.displayDepartureLabel = this.veriDate.formatDate(visit.ActualDepartureTime, "time");
            }
            else {
                visit.displayDepartureLabel = visit.status || "Planned";
            }

            lastVisit = visit;
        });
    }

    private updateRouteVisitTimes(visit: IRouteDetailVisitDisplay, lastVisit: IRouteDetailVisitDisplay, isFirstVisit: boolean, isFinalVisit: boolean) {

        const visitActualArrival = this.veriDate.createMoment(visit.ActualArrivalTime);
        const visitPlannedArrival = this.veriDate.createMoment(visit.PlannedArrivalTime);
        if (visit.ActualArrivalTime && visitPlannedArrival.diff(visitActualArrival, "m") !== 0) {
            visit.arrivalTimeDiff = visitPlannedArrival.from(visitActualArrival, true);
        }

        const visitActualDeparture = this.veriDate.createMoment(visit.ActualDepartureTime);
        const visitPlannedDeparture = this.veriDate.createMoment(visit.PlannedDepartureTime);


        if (visit.ActualDepartureTime && visitPlannedDeparture.diff(visitActualDeparture, "m") !== 0) {
            visit.departureTimeDiff = visitPlannedDeparture.from(visitActualDeparture, true);
        }

        //if ((visit.ActualArrivalTime || visit.ArrivalEta) && !isFirstVisit) {
        //    visit.arrivalLate = this.isArrivalLate(visit);
        //    visit.arrivalEarly = this.isArrivalEarly(visit);
        //}

        if (visit.ActualDepartureTime || visit.ActualArrivalTime || visit.ArrivalEta) {
            visit.departureLate = this.isDepartureLate(visit);
            visit.departureEarly = this.isDepartureEarly(visit);
        }

        //const plannedStopMins = this.veriDate.createMoment(visit.PlannedDepartureTime).diff(visit.PlannedArrivalTime, "m");

        //visit.displayArrivalTime = visit.ActualArrivalTime || visit.ArrivalEta || visit.PlannedArrivalTime; // Use actual arrival time if stop has been visited, expected if on way and planned if not yet in progress

        //if (visit.ActualDepartureTime) {
        //    visit.displayDepartureTime = visit.ActualDepartureTime; // Use actual departure time if stop has been visited and departed
        //}
        //else {
        //    if (visit.ActualArrivalTime && !visit.ActualDepartureTime) {
        //        //If vehicle is currently visiting stop, displayed planned departure should be updated to match
        //        visit.displayDepartureTime = this.veriDate.createMoment(visit.ActualArrivalTime).clone().add(plannedStopMins, "m").toDate();
        //    }
        //    else if (visit.ArrivalEta) {
        //        visit.displayDepartureTime = this.veriDate.createMoment(visit.ArrivalEta).clone().add(plannedStopMins, "m").toDate();
        //    } else {
        //        visit.displayDepartureTime = visit.PlannedDepartureTime;
        //    }
        //}


        //if (isFirstVisit && !visit.displayArrivalTime) {
        //    visit.displayArrivalTime = this.veriDate.createMoment(visit.displayDepartureTime).clone().add(-plannedStopMins, "m").toDate();
        //}
        //else if (isFinalVisit && !visit.displayDepartureTime) {
        //    visit.displayDepartureTime = this.veriDate.createMoment(visit.displayArrivalTime).clone().add(plannedStopMins, "m").toDate();
        //}
    }

    private updateRouteVisitState(visit: IRouteDetailVisitDisplay,
        lastVisit: IRouteDetailVisitDisplay,
        route: IRouteDetailDisplay,
        isFinalVisit: boolean) {

        visit.nextVisit = lastVisit && lastVisit.Status === RouteVisitStatus.Departed;

        if (visit.nextVisit) {
            route.nextVisit = visit;
        }

        //if (route.Status === RouteStatus.Started || route.Status === RouteStatus.Completed || route.Status === RouteStatus.CompletedWithMissing) {
        //    this.addClass(visit, "route-started");
        //}

        //this.setVisitStatusLabel(visit);

        //if (visit.Status === RouteVisitStatus.Departed ||
        //    visit.Status === RouteVisitStatus.Missed ||
        //    visit.Status === RouteVisitStatus.Arrived && isFinalVisit) {
        //    this.addClass(visit, "visit-completed");
        //}

        //if (visit.arrivalLate) {
        //    this.addClass(visit, "visit-late");
        //    visit.arrivalClassName = "visit-eta-red";
        //}
        //else {
        //    this.addClass(visit, "visit-ontime");
        //}

        //if (visit.arrivalEarly) {
        //    visit.arrivalClassName = "visit-eta-green";
        //}

        //if (visit.departureLate) {
        //    this.addClass(visit, "visit-late");
        //    visit.departureClassName = "visit-eta-red";
        //}
        //else {
        //    this.addClass(visit, "visit-ontime");
        //}

        //if (visit.departureEarly) {
        //    visit.departureClassName = "visit-eta-green";
        //}
    }

    //private addClass(visit: IRouteDetailVisitDisplay, className: string): void {
    //    visit.className += ` ${className}`;
    //}

    //private setVisitStatusLabel(visit: IRouteDetailVisitDisplay) {
    //    if (visit.Status === RouteVisitStatus.Arrived) {
    //        visit.status = "Arrived";
    //        this.addClass(visit, "visit-arrived");
    //    }

    //    if (visit.Status === RouteVisitStatus.Departed) {
    //        visit.status = "Departed";
    //        this.addClass(visit, "visit-departed");
    //    }

    //    if (visit.Status === RouteVisitStatus.Missed) {
    //        visit.status = "Missed";
    //        this.addClass(visit, "visit-missed");
    //    }

    //    if (visit.Status === RouteVisitStatus.Planned && visit.nextVisit) {
    //        visit.status = "En-Route";
    //        this.addClass(visit, "visit-enroute");
    //    }
    //}

    getVisitClass(visit: IRouteDetailVisitDisplay): string {
        let className = "";

        if (visit.Status === RouteVisitStatus.Missed) {
            return "missed";
        }

        if (visit.Status === RouteVisitStatus.Planned && visit.nextVisit) {
            className += "enroute";
        }

        if (this.isArrivalEarly(visit) || this.isDepartureEarly(visit)) {
            className += "early";
        }

        if (this.isArrivalLate(visit) || this.isDepartureLate(visit)) {
            className += "late";
        }

        if (this.isArrivalLate(visit) || this.isDepartureLate(visit)) {
            className += "late";
        }

        return className;
    }
}
